import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { viewModeAtomFamily, viewModeMemoryAtom, type ViewMode } from '~/atoms/atoms';
import { useAuthData } from '~/hooks/auth/use-auth-data';

const LEGACY_VIEW_MODE_KEYS = ['stablyViewMode', 'stably-view-mode'] as const;

const getViewModeScope = (orgId: string | undefined) => {
  if (orgId) {
    return `org-${orgId}`;
  }
  return undefined;
};

const isViewMode = (value: string | null): value is ViewMode => value === 'classic' || value === 'agent';

const getLegacyViewMode = (): ViewMode | undefined =>
  LEGACY_VIEW_MODE_KEYS.map((key) => window.localStorage.getItem(key)).find(isViewMode);

const removeLegacyViewModeKeys = () => {
  for (const key of LEGACY_VIEW_MODE_KEYS) {
    window.localStorage.removeItem(key);
  }
};

function useMigrateLegacyViewMode(scope: string | undefined, setScopedViewMode?: (mode: ViewMode) => void) {
  useEffect(() => {
    if (typeof window === 'undefined' || !scope) {
      return;
    }

    const scopedKey = `stably-view-mode-${scope}`;
    const existingScopedValue = window.localStorage.getItem(scopedKey);
    if (isViewMode(existingScopedValue)) {
      removeLegacyViewModeKeys();
      return;
    }

    const legacyValue = getLegacyViewMode();
    if (legacyValue) {
      if (setScopedViewMode) {
        setScopedViewMode(legacyValue);
      } else {
        window.localStorage.setItem(scopedKey, legacyValue);
      }
    }

    removeLegacyViewModeKeys();
  }, [scope, setScopedViewMode]);
}

function useMigrateProjectScopedViewModeToOrgScope(options: {
  scope: string | undefined;
  projectId: string | undefined;
  setScopedViewMode?: (mode: ViewMode) => void;
}) {
  const { scope, projectId, setScopedViewMode } = options;
  useEffect(() => {
    if (typeof window === 'undefined' || !scope || !projectId) {
      return;
    }

    const orgScopedKey = `stably-view-mode-${scope}`;
    const existingOrgScopedValue = window.localStorage.getItem(orgScopedKey);
    if (isViewMode(existingOrgScopedValue)) {
      return;
    }

    const projectScopedKey = `stably-view-mode-project-${projectId}`;
    const projectScopedValue = window.localStorage.getItem(projectScopedKey);
    if (!isViewMode(projectScopedValue)) {
      return;
    }

    if (setScopedViewMode) {
      setScopedViewMode(projectScopedValue);
    } else {
      window.localStorage.setItem(orgScopedKey, projectScopedValue);
    }
  }, [scope, projectId, setScopedViewMode]);
}

/**
 * Hook to get and set the view mode.
 *
 * @param projectId - Optional project ID used to migrate legacy project-scoped preference keys.
 * @returns A tuple of [viewMode, setViewMode] similar to useState
 */
export function useViewMode(projectId: string | undefined): [ViewMode, (mode: ViewMode) => void] {
  const { activeOrg } = useAuthData();
  const scope = getViewModeScope(activeOrg?.orgId);
  const [viewMode, setViewMode] = useAtom(scope ? viewModeAtomFamily(scope) : viewModeMemoryAtom);
  useMigrateProjectScopedViewModeToOrgScope({ scope, projectId, setScopedViewMode: scope ? setViewMode : undefined });
  useMigrateLegacyViewMode(scope, scope ? setViewMode : undefined);

  // Wrap setter to be a no-op when no project/org scope is available.
  const safeSetViewMode = useCallback(
    (mode: ViewMode) => {
      if (scope) {
        setViewMode(mode);
      }
    },
    [scope, setViewMode],
  );

  // Return 'classic' as default when no scope is available.
  if (!scope) {
    return ['classic', safeSetViewMode];
  }

  return [viewMode, safeSetViewMode];
}

/**
 * Hook to get the view mode (read-only).
 *
 * @param projectId - Optional project ID used to migrate legacy project-scoped preference keys.
 * @returns The current view mode ('classic' if projectId is undefined)
 */
export function useViewModeValue(projectId: string | undefined): ViewMode {
  const { activeOrg } = useAuthData();
  const scope = getViewModeScope(activeOrg?.orgId);
  const [viewMode, setViewMode] = useAtom(scope ? viewModeAtomFamily(scope) : viewModeMemoryAtom);
  useMigrateProjectScopedViewModeToOrgScope({ scope, projectId, setScopedViewMode: scope ? setViewMode : undefined });
  useMigrateLegacyViewMode(scope, scope ? setViewMode : undefined);

  if (!scope) {
    return 'classic';
  }

  return viewMode;
}
